/** Free-text parameter chip (S10) — input popover, commit on Apply/Enter. */
import { useEffect, useState } from 'react';
import { Type } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { BoundParams } from '../../types/dashboardTypes';
import { ParamChipButton } from './ParamChipButton';

type Props = {
  id: string;
  label: string;
  defaultValue?: string;
  value: BoundParams[string] | undefined;
  onChange: (next: string) => void;
};

export function FreeTextChip({ id, label, defaultValue, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const committed =
    value === undefined || (typeof value === 'object' && !Array.isArray(value))
      ? (defaultValue ?? '')
      : String(Array.isArray(value) ? value[0] : value);
  const [draft, setDraft] = useState(committed);
  useEffect(() => {
    if (open) setDraft(committed);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset draft on open only
  }, [open]);

  const commit = () => {
    onChange(draft);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <ParamChipButton
          icon={<Type className="h-3 w-3" />}
          label={label}
          value={committed || '—'}
          testId={`param-chip-${id}`}
        />
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="flex gap-1.5">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && commit()}
            className="h-7 text-xs"
            data-testid={`param-input-${id}`}
            aria-label={label}
          />
          <Button size="sm" className="h-7 text-xs" onClick={commit} data-testid={`param-apply-${id}`}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
